import { useEffect, useState } from "react";

import { apiClient } from "../../../services/api/apiClient";

export interface AssignableAuditor {
  id: number;
  name: string;
  code?: string | null;
}

interface UseAssignableAuditorsArgs {
  visible: boolean;
  /** Audit company of the planning group. When set, the dropdown is
   *  restricted to its members. */
  auditCompanyId?: number | null;
  /** Country (master_data.Country.id) of the campaign. The backend maps
   *  it to a portfolio code and filters by `portfolio` + `allowed_portfolios`. */
  countryId?: number | null;
  /** When true (current user is a plain auditor, type=5), the dropdown is
   *  locked to the user themselves — they can only attribute the upload to
   *  themselves. */
  isAuditorOnly: boolean;
  /** Identity used for the auditor-only branch. */
  currentUser?: { id: number; name: string } | null;
  /** Fallback list shown until the fetch resolves (or when no audit
   *  company is set). Usually `record.users` from the planning. */
  fallback?: AssignableAuditor[];
}

/**
 * Shared loader for the auditor Select used by Route/Waypoint
 * upload/edit modals.
 *
 * Returns the list of auditors the current user is allowed to assign
 * work to, with the same scoping as the audit-assignment dropdown:
 * same `audit_company` AND matching portfolio/country. For a regular
 * auditor (user_type=5) the list collapses to themselves.
 */
export function useAssignableAuditors({
  visible,
  auditCompanyId,
  countryId,
  isAuditorOnly,
  currentUser,
  fallback,
}: UseAssignableAuditorsArgs) {
  const [loaded, setLoaded] = useState<AssignableAuditor[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || isAuditorOnly) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page_size: "500" });
        if (auditCompanyId != null) {
          params.set("audit_company", String(auditCompanyId));
        }
        if (countryId != null) {
          params.set("country", String(countryId));
        }
        const res = await apiClient.get(`/app-users/?${params.toString()}`);
        const list = res.data?.results ?? res.data?.data ?? res.data ?? [];
        if (!cancelled) setLoaded(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setLoaded([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, isAuditorOnly, auditCompanyId, countryId]);

  // Auditor (type=5) — locked to themselves. Don't even render anyone else.
  if (isAuditorOnly && currentUser?.id != null) {
    return {
      loading: false,
      locked: true,
      defaultValue: currentUser.id,
      options: [
        {
          value: currentUser.id,
          label:
            currentUser.name ||
            // Defensive fallback if identity has no name; matches the
            // backend behaviour of using `#<id>` for unresolved users.
            `#${currentUser.id}`,
        },
      ],
    };
  }

  const merged = (() => {
    const map = new Map<number, AssignableAuditor>();
    for (const a of fallback || []) map.set(a.id, a);
    for (const a of loaded || []) map.set(a.id, a);
    return Array.from(map.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  })();

  return {
    loading,
    locked: false,
    defaultValue: undefined as number | undefined,
    options: merged.map((a) => ({
      value: a.id,
      label: a.code ? `${a.name} (${a.code})` : a.name,
    })),
  };
}
